import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Filter,
  HandshakeIcon,
  Plus,
  Shield,
  X,
  XCircle,
} from 'lucide-react';
import { Navigate, useParams } from 'react-router-dom';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { formatCurrency, formatDate } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type {
  SettlementResponseDto,
  SettlementStatus,
} from '@splitfiesta/shared';
import { SETTLEMENT_STATUS } from '@splitfiesta/shared';
import { Alert, Badge, Button, Card } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { CreateSettlementModal } from '@/components/groups/CreateSettlementModal';
import { GroupPageLayout } from '@/components/groups/GroupPageLayout';
import { RecordPaymentModal } from '@/components/groups/RecordPaymentModal';

import { useGroupWithDashboardQuery } from '@/hooks/queries/useGroups';
import {
  useCancelSettlementMutation,
  useConfirmSettlementMutation,
  useGroupSettlementsInfiniteQuery,
  useRejectSettlementMutation,
} from '@/hooks/queries/useSettlements';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useToast } from '@/hooks/useToast';

import { resolveUserName } from '@/lib/displayName';

import { useAuthStore } from '@/stores/authStore';

import { ITEMS_PER_PAGE } from '@/constants/pagination';
import { celebrate } from '@/utils/confetti';

function GroupSettlementsPage() {
  const { id: groupId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const user = useAuthStore(state => state.user);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] =
    useState(false);
  const [statusFilter, setStatusFilter] = useState<SettlementStatus | 'all'>(
    'all'
  );
  const [showFilters, setShowFilters] = useState(false);
  const [settlementToCancel, setSettlementToCancel] = useState<{
    id: string;
    isCompleted: boolean;
  } | null>(null);
  const [settlementToConfirm, setSettlementToConfirm] = useState<string | null>(
    null
  );
  const [settlementToReject, setSettlementToReject] = useState<string | null>(
    null
  );

  // Load required namespaces - must be before any conditional returns
  const groupsLoaded = useNamespace('groups');
  const settlementsLoaded = useNamespace('settlements');
  const notificationsLoaded = useNamespace('notifications');

  const allNamespacesLoaded =
    groupsLoaded && settlementsLoaded && notificationsLoaded;

  // Get group data - this will be cached from GroupPageLayout
  const { data: group } = useGroupWithDashboardQuery(
    groupId || '',
    allNamespacesLoaded && !!user && !!groupId
  );

  // Fetch settlements with infinite scroll
  const {
    data: settlementsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useGroupSettlementsInfiniteQuery(
    groupId || '',
    {
      limit: ITEMS_PER_PAGE,
      status: statusFilter === 'all' ? undefined : statusFilter,
    },
    !!group
  );

  // Refetch when filter changes
  useEffect(() => {
    refetch();
  }, [statusFilter, refetch]);

  // Process settlements data
  const settlements = useMemo((): SettlementResponseDto[] => {
    if (!settlementsData?.pages) return [];
    return settlementsData.pages.flatMap(page => {
      // Handle both direct settlements array and paginated response
      if (page && typeof page === 'object' && 'settlements' in page) {
        return page.settlements as SettlementResponseDto[];
      } else if (page && typeof page === 'object' && 'items' in page) {
        return page.items as SettlementResponseDto[];
      }
      return [];
    });
  }, [settlementsData]);

  // Settlement mutations
  const confirmMutation = useConfirmSettlementMutation();
  const cancelMutation = useCancelSettlementMutation();
  const rejectMutation = useRejectSettlementMutation();

  // Load more settlements for pagination
  const loadMoreSettlements = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Infinite scroll hook
  const lastSettlementElementRef = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasMore: hasNextPage,
    onLoadMore: loadMoreSettlements,
  });

  const handleConfirmSettlement = (settlementId: string) => {
    setSettlementToConfirm(settlementId);
  };

  const handleConfirmSettlementConfirm = async () => {
    if (!settlementToConfirm || !groupId) return;

    try {
      await confirmMutation.mutateAsync({
        settlementId: settlementToConfirm,
        groupId,
      });

      // Celebrate with confetti when recipient confirms settlement
      celebrate('high');

      toast({
        title: t('settlements:messages.settlementConfirmed'),
        type: 'success',
      });
      setSettlementToConfirm(null);
    } catch (_error) {
      toast({
        title: t('common:errors.genericError'),
        type: 'error',
      });
    }
  };

  const handleConfirmDialogClose = () => {
    setSettlementToConfirm(null);
  };

  const handleCancelSettlement = (
    settlementId: string,
    isCompleted: boolean
  ) => {
    setSettlementToCancel({ id: settlementId, isCompleted });
  };

  const handleConfirmCancel = async () => {
    if (!settlementToCancel || !groupId) return;

    try {
      await cancelMutation.mutateAsync({
        settlementId: settlementToCancel.id,
        groupId,
      });
      toast({
        title: t('settlements:messages.settlementCancelled'),
        type: 'success',
      });
      setSettlementToCancel(null);
    } catch (_error) {
      toast({
        title: t('common:errors.genericError'),
        type: 'error',
      });
    }
  };

  const handleCancelDialogClose = () => {
    setSettlementToCancel(null);
  };

  const handleRejectSettlement = (settlementId: string) => {
    setSettlementToReject(settlementId);
  };

  const handleRejectSettlementConfirm = async () => {
    if (!settlementToReject || !groupId) return;

    try {
      await rejectMutation.mutateAsync({
        settlementId: settlementToReject,
        groupId,
      });
      toast({
        title: t('settlements:messages.settlementRejected'),
        type: 'success',
      });
      setSettlementToReject(null);
    } catch (_error) {
      toast({
        title: t('common:errors.genericError'),
        type: 'error',
      });
    }
  };

  const handleRejectDialogClose = () => {
    setSettlementToReject(null);
  };

  const getStatusBadge = (status: SettlementStatus) => {
    switch (status) {
      case SETTLEMENT_STATUS.PENDING:
        return (
          <Badge
            variant="warning"
            className="inline-flex items-center gap-1 shrink-0"
          >
            <Clock className="h-3 w-3" />
            {t('settlements:status.pending')}
          </Badge>
        );
      case SETTLEMENT_STATUS.COMPLETED:
        return (
          <Badge
            variant="success"
            className="inline-flex items-center gap-1 shrink-0"
          >
            <CheckCircle2 className="h-3 w-3" />
            {t('settlements:status.completed')}
          </Badge>
        );
      case SETTLEMENT_STATUS.REJECTED:
        return (
          <Badge
            variant="danger"
            className="inline-flex items-center gap-1 shrink-0"
          >
            <XCircle className="h-3 w-3" />
            {t('settlements:status.rejected')}
          </Badge>
        );
      case SETTLEMENT_STATUS.CANCELLED:
        return (
          <Badge
            variant="neutral"
            className="inline-flex items-center gap-1 shrink-0"
          >
            <XCircle className="h-3 w-3" />
            {t('settlements:status.cancelled')}
          </Badge>
        );
      default:
        return null;
    }
  };

  const renderSettlementCard = (
    settlement: SettlementResponseDto,
    index: number
  ) => {
    const isLastElement = index === settlements.length - 1;
    const isPayer = settlement.fromUser.id === user?.id;
    const isRecipient = settlement.toUser.id === user?.id;
    const isCancelled = settlement.status === SETTLEMENT_STATUS.CANCELLED;
    const isRejected = settlement.status === SETTLEMENT_STATUS.REJECTED;
    const isInactive = isCancelled || isRejected;

    return (
      <Card
        key={settlement.id}
        ref={isLastElement ? lastSettlementElementRef : null}
        data-testid={`settlement-card-${settlement.id}`}
        className={`p-4 sm:p-6 transition-all duration-200 ${
          isInactive
            ? 'opacity-65 hover:opacity-80 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50'
            : ''
        }`}
        padding="none"
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
              <div className="flex-1">
                <p
                  className={`font-medium text-gray-900 dark:text-gray-100 ${
                    isInactive
                      ? 'line-through decoration-2 decoration-gray-500'
                      : ''
                  }`}
                >
                  {isPayer
                    ? t('settlements:youPaid', {
                        name: resolveUserName(settlement.toUser?.name, t),
                      })
                    : isRecipient
                      ? t('settlements:paidYou', {
                          name: resolveUserName(settlement.fromUser?.name, t),
                        })
                      : t('settlements:paidTo', {
                          from: resolveUserName(settlement.fromUser?.name, t),
                          to: resolveUserName(settlement.toUser?.name, t),
                        })}
                </p>
              </div>
              <div className="self-start sm:ml-3">
                {getStatusBadge(settlement.status)}
              </div>
            </div>
            <p
              className={`text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 ${
                isInactive
                  ? 'line-through decoration-4 decoration-red-400 dark:decoration-red-500'
                  : ''
              }`}
            >
              {formatCurrency(settlement.amount, settlement.currency)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {formatDate(settlement.createdAt)}
            </p>
            {settlement.notes && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 wrap-break-word">
                {settlement.notes}
              </p>
            )}
            {/* Show indicator when settlement was recorded by someone other than the payer (e.g., group owner) */}
            {settlement.createdBy &&
              settlement.createdBy.id !== settlement.fromUser.id && (
                <div className="flex items-center gap-1.5 mt-2">
                  <Badge
                    variant="neutral"
                    size="sm"
                    className="inline-flex items-center gap-1"
                  >
                    <Shield className="h-3 w-3" />
                    {t('settlements:labels.recordedBy', {
                      name: resolveUserName(settlement.createdBy.name, t),
                    })}
                  </Badge>
                </div>
              )}
          </div>

          {/* Buttons for RECIPIENT (can confirm or reject) */}
          {settlement.status === SETTLEMENT_STATUS.PENDING && isRecipient && (
            <div className="flex flex-row sm:flex-col gap-2 sm:gap-1.5 w-full sm:w-auto sm:min-w-[140px]">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRejectSettlement(settlement.id)}
                disabled={rejectMutation.isPending}
                data-testid={`reject-settlement-${settlement.id}`}
                className="flex-1 sm:flex-initial w-full min-h-[40px]"
              >
                {rejectMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <span className="truncate">
                    {t('settlements:actions.rejectSettlement')}
                  </span>
                )}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => handleConfirmSettlement(settlement.id)}
                disabled={confirmMutation.isPending}
                data-testid={`confirm-settlement-${settlement.id}`}
                className="flex-1 sm:flex-initial w-full min-h-[40px]"
              >
                {confirmMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <span className="truncate">
                    {t('settlements:actions.confirmSettlement')}
                  </span>
                )}
              </Button>
            </div>
          )}

          {/* Button for users who can cancel (sender for pending, owner for completed) */}
          {settlement.canCancel && (
            <div className="flex w-full sm:w-auto sm:min-w-[140px]">
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  handleCancelSettlement(
                    settlement.id,
                    settlement.status === SETTLEMENT_STATUS.COMPLETED
                  )
                }
                disabled={cancelMutation.isPending}
                data-testid={`cancel-settlement-${settlement.id}`}
                className="w-full min-h-[40px]"
              >
                {cancelMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <span className="truncate">
                    {t('settlements:actions.cancelSettlement')}
                  </span>
                )}
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  };

  if (!groupId) {
    return <Navigate to="/groups" replace />;
  }

  return (
    <GroupPageLayout groupId={groupId}>
      <div data-testid="settlements-container">
        {/* Header with Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              data-testid="create-settlement-button"
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2 shrink-0" />
              <span className="truncate">
                {t('settlements:createSettlement')}
              </span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsRecordPaymentModalOpen(true)}
              data-testid="record-payment-button"
              className="w-full sm:w-auto"
            >
              <HandshakeIcon className="h-4 w-4 mr-2 shrink-0" />
              <span className="truncate">
                {t('settlements:actions.recordPayment')}
              </span>
            </Button>
          </div>

          {/* Filter Toggle Button */}
          <Button
            variant={
              statusFilter !== 'all' || showFilters
                ? 'primary'
                : 'outline-solid'
            }
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={
              showFilters ? (
                <X className="h-4 w-4" />
              ) : (
                <Filter className="h-4 w-4" />
              )
            }
            className="relative w-full sm:w-auto"
            data-testid="toggle-filters"
          >
            {showFilters
              ? t('notifications:hide_filters')
              : t('notifications:show_filters')}
            {statusFilter !== 'all' && !showFilters && (
              <span className="absolute -top-1 -right-1 h-2 w-2 bg-primary-500 rounded-full" />
            )}
          </Button>
        </div>

        {/* Collapsible Filter Panel */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showFilters ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'
          }`}
        >
          <Card className="border-2 border-primary-200 dark:border-primary-900/50">
            <div className="flex flex-wrap gap-2">
              <Button
                variant={statusFilter === 'all' ? 'primary' : 'outline-solid'}
                size="sm"
                onClick={() => setStatusFilter('all')}
                data-testid="filter-all"
              >
                {t('settlements:filters.all')}
              </Button>
              <Button
                variant={
                  statusFilter === SETTLEMENT_STATUS.PENDING
                    ? 'primary'
                    : 'outline-solid'
                }
                size="sm"
                onClick={() => setStatusFilter(SETTLEMENT_STATUS.PENDING)}
                data-testid="filter-pending"
              >
                {t('settlements:filters.pending')}
              </Button>
              <Button
                variant={
                  statusFilter === SETTLEMENT_STATUS.COMPLETED
                    ? 'primary'
                    : 'outline-solid'
                }
                size="sm"
                onClick={() => setStatusFilter(SETTLEMENT_STATUS.COMPLETED)}
                data-testid="filter-completed"
              >
                {t('settlements:filters.completed')}
              </Button>
              <Button
                variant={
                  statusFilter === SETTLEMENT_STATUS.REJECTED
                    ? 'primary'
                    : 'outline-solid'
                }
                size="sm"
                onClick={() => setStatusFilter(SETTLEMENT_STATUS.REJECTED)}
                data-testid="filter-rejected"
              >
                {t('settlements:filters.rejected')}
              </Button>
              <Button
                variant={
                  statusFilter === SETTLEMENT_STATUS.CANCELLED
                    ? 'primary'
                    : 'outline-solid'
                }
                size="sm"
                onClick={() => setStatusFilter(SETTLEMENT_STATUS.CANCELLED)}
                data-testid="filter-cancelled"
              >
                {t('settlements:filters.cancelled')}
              </Button>
            </div>
          </Card>
        </div>

        {/* Settlements List */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <Alert
            type="error"
            icon={AlertCircle}
            message={t('common:errors.loadFailed')}
          />
        ) : settlements.length === 0 ? (
          <Card
            data-testid="empty-settlements-card"
            className="p-6"
            padding="none"
          >
            <EmptyState
              icon={HandshakeIcon}
              title={t('settlements:noSettlements')}
              description={t('settlements:noSettlementsDescription')}
              className="py-8"
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {settlements.map((settlement, index) =>
              renderSettlementCard(settlement, index)
            )}
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            )}
          </div>
        )}

        {/* Create Settlement Modal */}
        {group && (
          <CreateSettlementModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            group={group}
          />
        )}

        {/* Record Payment Modal (creditor-initiated settlement) */}
        {group && (
          <RecordPaymentModal
            isOpen={isRecordPaymentModalOpen}
            onClose={() => setIsRecordPaymentModalOpen(false)}
            group={group}
          />
        )}

        {/* Confirmation Dialog for Cancelling Settlement */}
        <ConfirmationDialog
          isOpen={!!settlementToCancel}
          onClose={handleCancelDialogClose}
          onConfirm={handleConfirmCancel}
          title={t('settlements:confirmDialog.cancelTitle')}
          description={
            settlementToCancel?.isCompleted
              ? t('settlements:confirmDialog.cancelDescriptionAsOwner')
              : t('settlements:confirmDialog.cancelDescription')
          }
          confirmText={t('settlements:confirmDialog.confirmCancel')}
          cancelText={t('settlements:confirmDialog.keepSettlement')}
          variant="warning"
          isLoading={cancelMutation.isPending}
        />

        {/* Confirmation Dialog for Confirming Settlement */}
        <ConfirmationDialog
          isOpen={!!settlementToConfirm}
          onClose={handleConfirmDialogClose}
          onConfirm={handleConfirmSettlementConfirm}
          title={t('settlements:confirmDialog.confirmTitle')}
          description={t('settlements:confirmDialog.confirmDescription')}
          confirmText={t('settlements:confirmDialog.confirmSettlement')}
          cancelText={t('common:actions.cancel')}
          variant="default"
          isLoading={confirmMutation.isPending}
        />

        {/* Confirmation Dialog for Rejecting Settlement */}
        <ConfirmationDialog
          isOpen={!!settlementToReject}
          onClose={handleRejectDialogClose}
          onConfirm={handleRejectSettlementConfirm}
          title={t('settlements:confirmDialog.rejectTitle')}
          description={t('settlements:confirmDialog.rejectDescription')}
          confirmText={t('settlements:confirmDialog.confirmReject')}
          cancelText={t('common:actions.cancel')}
          variant="danger"
          isLoading={rejectMutation.isPending}
        />
      </div>
    </GroupPageLayout>
  );
}

export default withAuth(GroupSettlementsPage);
